import React, { useState, useEffect } from "react";
import Header from "../Header";
import Button from "../Button";
import styles from "./ReviewAddress.module.css";
import { useHistory, useParams } from "react-router-dom";
import { useAccount } from "utils/AccountInfoContext";
import { useAddresses } from "AddressesPage/useAddressesPage";
import { ComparisonModal } from "../ComparisonModal/ComparisonModal";
import AddressModal from "components/AddressModal";
import useReviewAddress from "./useReviewAddress";
import { useTranslation } from "react-i18next";

export function ReviewAddress() {
  const {t} = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { statesData, subEntityData, cachedIBOInfo } = useAccount();
  const { addResolveAddress } = useAddresses({ fetchAddresses: false });
  const { mutateAsync: confirmAddress } = useReviewAddress();

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [originalAddress, setOriginalAddress] = useState(null);
  const [validationFailed, setValidationFailed] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [accountName, setAccountName] = useState("");
  const [addressStatus, setAddressStatus] = useState(null);
  const [primaryAddress, setPrimaryAddress] = useState(null);
  const [usePrimary, setUsePrimary] = useState(false);
  const pinLevelMapping = {
    2: "Platinum Settings",
    3: "Emerald Settings",
    4: "Diamond Settings",
  };

  const pinLevel = cachedIBOInfo?.PinLevel || 0;
  const headerText = pinLevelMapping[pinLevel];
  useEffect(() => {
    const selectedItem = subEntityData.find(
      (item) => item.entityId === parseInt(id)
    );
    if (selectedItem) {
      setSelectedAddress(selectedItem.address);
      setOriginalAddress(selectedItem.address);
      setAccountName(selectedItem.bankInfo?.nameOnAccount || selectedItem.name);
      setAddressStatus(selectedItem.addressStatus);
      setPrimaryAddress(selectedItem.primaryAddress);
    }
  }, [id, subEntityData]);

  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const [suggestedAddress, setSuggestedAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [isAddInComparison, setIsAddInComparison] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(true);
  const [loading, setLoading] = useState(false);

  const capitalizeFirstLetter = (string) => {
    return string.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatPostalCode = (postalCode) => {
    return postalCode ? postalCode.slice(0, 5) : "";
  };

  let updatedCountry = address.country;
  let updatedState = address.state;

  if (address.country === "United States") updatedCountry = "US";
  if (address.state.includes(" - ")) {
    updatedState = address.state.split(" - ")[0];
  } else {
    updatedState = address.state.substring(0, 2).toUpperCase();
  }

  async function handleResolveAddress(address, isAdd) {
    const resolveAddressData = {
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      country: address.country,
      postalCode: address.postalCode,
    };

    setLoading(true);

    try {
      const resolvedData = await addResolveAddress.mutateAsync(
        resolveAddressData
      );

      const transformedAddress = {
        line1: capitalizeFirstLetter(resolvedData.line1),
        line2: resolvedData.line2 || address.line2 || null,
        city: resolvedData.city,
        state: resolvedData.state,
        country: resolvedData.country,
        postalCode: resolvedData.postalCode,
        latitude: resolvedData.latitude || address.latitude || 0,
        longitude: resolvedData.longitude || address.longitude || 0,
        suggestions: resolvedData.suggestions || [],
      };

      if (transformedAddress.suggestions.length > 0) {
        const errors = {};
        if (
          transformedAddress.suggestions.includes(
            t("Invalid PO Box or House Number")
          )
        ) {
          errors.line1 = true;
        }
        setValidationErrors({
          ...errors,
          suggestions: transformedAddress.suggestions,
        });
        setValidationFailed(true);
        setLoading(false);
        return;
      }

      setAddress((prevState) => {
        return isAdd
          ? {
              ...prevState,
              ...address,
            }
          : {
              ...prevState,
              ...selectedAddress,
              ...address,
            };
      });

      setSuggestedAddress({
        ...transformedAddress,
        isConfirmed: 1,
      });
      setLoading(false);

      setShowModal(false);
      setShowComparisonModal(true);
      setIsAddInComparison(isAdd);
    } catch (error) {
      console.error(t("Error resolving address"), error);
      setLoading(false);
    }
  }

  async function handleUpdateAddress(address) {
    if (!address.iboAddressId) {
      return;
    }

    const updatedData = {
      line1: address.line1 || "",
      line2: address.line2 || null,
      city: address.city || "",
      state: address.state || "",
      country: address.country || "",
      postalCode: address.postalCode || "",
      recipient: address.recipient || "",
      latitude: address.latitude || 0,
      longitude: address.longitude || 0,
      isConfirmed: address.isConfirmed || 0,
      types: {
        primary: address.primary || 0,
        mailing: address.mailing || 0,
        shipping: address.shipping || 0,
        billing: address.billing || 0,
        bitwiseType: address.billing,
      },
    };

    try {
      // await updateAddress.mutateAsync({
      //   iboAddressId: address.iboAddressId,
      //   updateData: updatedData,
      // });
    } catch (error) {
      console.error(t("Error updating address"), error);
    }

    setIsAddInComparison(false);
    resetForm();
  }

  const handleValidateAddress = async (address, isAdd) => {
    const validateAddress = (address, suggestions) => {
      const errors = {};

      if (
        !address.line1 ||
        (suggestions.length > 0 &&
          suggestions.includes(t("Invalid PO Box or House Number")))
      ) {
        errors.line1 = true;
      }

      if (!suggestions.includes(t("Invalid PO Box or House Number"))) {
        if (!address.city) {
          errors.city = true;
        }

        if (address.state === t("Select State") || !address.state) {
          errors.state = true;
        }

        if (address.country === t("Select Country") || !address.country) {
          errors.country = true;
        }

        if (!address.postalCode) {
          errors.postalCode = true;
        }
      }

      const hasFieldErrors = Object.keys(errors).some((key) => errors[key]);

      if (hasFieldErrors) {
        if (!suggestions || suggestions.length === 0) {
          suggestions = address.suggestions;
        }
        setValidationFailed(true);
        setValidationErrors({ ...errors, suggestions });
      } else {
        setValidationErrors([]);
      }

      return errors;
    };

    let suggestions = [];
    const errors = validateAddress(address, suggestions);

    if (Object.keys(errors).some((key) => errors[key])) {
      setValidationErrors(errors);
      setValidationFailed(true);
    } else {
      setValidationFailed(false);
      setValidationErrors([]);
      await handleResolveAddress(address, isAdd);
    }
  };

  const handleAddressChange = (field, value) => {
    setSelectedAddress((prevAddress) => ({
      ...prevAddress,
      [field]: value,
    }));

    setShowComparisonModal(false);
  };

  const handleSkipClick = () => {
    const selectedItem = subEntityData.find(
      (item) => item.entityId === parseInt(id)
    );

    if (selectedItem) {
      history.push(`/review-email/${selectedItem.entityId}`);
    } else {
      console.error(t("Selected item not found"));
    }
  };

  const handleAddressConfirmClick = async () => {
    const addressToConfirm = usePrimary ? primaryAddress : selectedAddress;

    const { latitude, longitude, suggestions, ...addressWithoutLatLong } =
      addressToConfirm;

    try {
      await confirmAddress({ entityId: id, address: addressWithoutLatLong });
      const selectedEntity = subEntityData.find(
        (item) => item.entityId === parseInt(id)
      );

      if (selectedEntity) {
        history.push(`/review-email/${selectedEntity.entityId}`);
      } else {
        console.error(t("Selected item not found"));
      }
    } catch (error) {
      console.error(t("Error confirming address"), error);
    }
  };

  const handleUpdateAddressClick = () => {
    setShowModal(true);
    setShowComparisonModal(false);
    setIsAddingAddress(false);
  };

  const handleAddAddressClick = () => {
    setShowModal(true);
    setShowComparisonModal(false);
    setSelectedAddress(address);
    setIsAddingAddress(true);
  };

  const handleUseOriginal = async () => {
    const { latitude, longitude, suggestions, ...addressWithoutLatLong } =
      selectedAddress;

    try {
      await confirmAddress({ entityId: id, address: addressWithoutLatLong });

      const selectedItem = subEntityData.find(
        (item) => item.entityId === parseInt(id)
      );

      if (selectedItem) {
        history.push(`/review-email/${selectedItem.entityId}`);
      } else {
        console.error(t("Selected item not found"));
      }
    } catch (error) {
      console.error(t("Error confirming address"), error);
    }
  };

  const handleUsePrimary = () => {
    setSelectedAddress(primaryAddress);
    setUsePrimary(true);
  };

  const handleUseSuggested = async () => {
    const { city, country, state, postalCode, line1, line2 } = suggestedAddress;

    const filteredAddress = {
      city,
      country,
      state,
      postalCode,
      line1,
      line2,
    };

    try {
      await confirmAddress({ entityId: id, address: filteredAddress });

      const selectedItem = subEntityData.find(
        (item) => item.entityId === parseInt(id)
      );

      if (selectedItem) {
        history.push(`/review-email/${selectedItem.entityId}`);
      } else {
        console.error(t("Selected item not found"));
      }
    } catch (error) {
      console.error(t("Error confirming address"), error);
    }
  };

  const handleEditClick = () => {
    setShowComparisonModal(false);
    setShowModal(true);
    setValidationErrors([]);
    setValidationFailed(false);
    setIsAddingAddress(false);
  };

  const handleOnCancel = () => {
    setShowComparisonModal(false);
    setShowModal(false);
    setValidationErrors([]);
    setValidationFailed(false);
    setUsePrimary(false);
    setSelectedAddress(originalAddress);
  };

  const handleComparisonEditClick = () => {
    setShowComparisonModal(false);
    setShowModal(true);

    setIsAddingAddress(false);
    setIsAddInComparison(true);
  };

  const handleComparisonCancelClick = () => {
    resetForm();
    setShowComparisonModal(false);
    setUsePrimary(false);
  };

  const resetForm = () => {
    setSelectedAddress(null);
    setValidationErrors({});
    setValidationFailed(false);
    setUsePrimary(false);
  };

  const handleCancel = () => {
    setShowModal(false);
    setShowComparisonModal(false);
    setValidationFailed(false);
    setUsePrimary(false);
    setSelectedAddress(originalAddress);
  };

  const shouldShowPrimaryAddress =
    (addressStatus === 1 ||
      addressStatus === 2 ||
      addressStatus === 6 ||
      addressStatus === 7) &&
    primaryAddress &&
    JSON.stringify(primaryAddress) !== JSON.stringify(selectedAddress);
  const handleHeaderClick = () => {
    history.push("/platinum-settings");
  };
  return (
    <div className={styles.container}>
      <Header header={t(headerText)} onClick={handleHeaderClick} />

      <div className={styles.flexContainer}>
        <span className={styles.header}>{t("Review Address")}</span>
        <Button onClick={handleSkipClick} buttonText={t("Skip")} className={styles.skipButton}/>
      </div>
      <span className={styles.addressLabel}>{accountName}</span>
      {addressStatus === 3 && !selectedAddress ? (
        <div className={styles.addressBody}>
          <div className={styles.noAddress}>{t("No address on file")}</div>
          <Button
            onClick={handleAddAddressClick}
            buttonText={t("Add Entity Address")}
          />
          {primaryAddress && (
            <>
              <div className={styles.addressBodyWithBorder}></div>
              <div className={styles.addressLabel}>
                {t("Your Primary Address on file")}
              </div>
              <div className={styles.primaryAddressBody}>
                <div>{primaryAddress.line1}</div>
                <div>{primaryAddress.line2}</div>
                <div>
                  {primaryAddress.city}, {primaryAddress.state}{" "}
                  {primaryAddress.postalCode}
                </div>
              </div>
              <div className={styles.buttonsContainer}>
                <Button
                  onClick={handleUsePrimary}
                  buttonText={t("Use Primary Address")}
                  className={styles.btnConfirm}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        selectedAddress && (
          <>
            <div className={styles.addressBody}>
              <div>{selectedAddress.line1}</div>
              <div>{selectedAddress.line2}</div>
              <div>
                {selectedAddress.city}, {selectedAddress.state}{" "}
                {selectedAddress.postalCode}
              </div>
              {!usePrimary && (
                <div className={styles.buttonsContainer}>
                  <Button
                    onClick={handleAddressConfirmClick}
                    buttonText={t("Address is Correct")}
                  />
                  <Button
                    onClick={handleUpdateAddressClick}
                    buttonText={t("Update Address")}
                    className={styles.updateButton}
                  />
                </div>
              )}
            </div>
            {usePrimary ? (
              <div className={styles.buttonsContainer}>
                <Button
                  onClick={handleAddressConfirmClick}
                  buttonText={t("Confirm Address Change")}
                />
                <Button
                  onClick={handleOnCancel}
                  buttonText={t("Cancel")}
                  className={styles.btnCancel}
                />
              </div>
            ) : (
              shouldShowPrimaryAddress && (
                <>
                  <div className={styles.addressBodyWithBorder}></div>
                  <div className={styles.addressLabel}>
                    {t("Your Primary Address on file")}
                  </div>
                  <div className={styles.addressBody}>
                    <div>{primaryAddress.line1}</div>
                    <div>{primaryAddress.line2}</div>
                    <div>
                      {primaryAddress.city}, {primaryAddress.state}{" "}
                      {primaryAddress.postalCode}
                    </div>
                  </div>
                  <div className={styles.buttonsContainer}>
                    <Button
                      onClick={handleUsePrimary}
                      buttonText={t("Use Primary Address")}
                      className={`${styles.button} ${styles.btnConfirm} ${styles.useButton}`}
                    />
                  </div>
                </>
              )
            )}
          </>
        )
      )}

      {showComparisonModal && (
        <ComparisonModal
          isOpen={showComparisonModal}
          title={t("Please Confirm Your Address")}
          onClose={handleOnCancel}
          suggestedAddress={suggestedAddress}
          address={selectedAddress}
          handleUseSuggested={handleUseSuggested}
          handleUseOriginal={handleUseOriginal}
          handleEditClick={handleEditClick}
        />
      )}

      <AddressModal
        title={isAddingAddress ? t("Add Address") : t("Edit Address")}
        isAdd={isAddingAddress}
        isOpen={showModal}
        address={selectedAddress}
        states={statesData}
        onCancel={handleCancel}
        onValidate={(address) =>
          handleValidateAddress(address, isAddingAddress)
        }
        onChange={handleAddressChange}
        validationFailed={validationFailed}
        validationErrorsArray={validationErrors}
        loading={loading}
      />
    </div>
  );
}

export default ReviewAddress;
